import * as React from "react";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { Accordion } from "../../Elements/Accordion";
import { useDrawingContext } from "../../Contexts/DrawingContext";
import { DrawPoints } from "./DrawPoints";
import { DrawPolylines } from "./DrawPolylines";
import { DrawPolygons } from "./DrawPolygons";
import { useRegistrationContext } from "../../Contexts/RegistrationContext";
import { DrawType, PageId } from "../../Map/enums";
import { config } from "../../Map/config";
import { getMap } from "../DrawingForm";
import { Search } from "./Search";
import { AttachDrawing } from "./AttachDrawing";
import { AccordionInfo } from "../../Elements/AccordionInfo";
import { getIntersectedForestries, getIntersectedForestryCount, getLVMForestry } from "../../Map/rest";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import "./DrawingTools.scss";

const formId = PageId.DrawingForm;

const infoStrings = {
    scaleForPointsWarning: "Šī objekta iezīmēšana kartē ir iespējama mērogā no 500 m, lūdzu pietuviniet karti!",
    scaleForLineAndAreaWarning: "Šī objekta iezīmēšana kartē ir iespējama mērogā no 2 km, lūdzu pietuviniet karti!",
    missingDrawingsWarning: "Jābūt norādītai pulcēšanās vietai!",
    remoteEventWarning: "Kartē jāatzīmē vismaz viens objekts!"
};

export const DrawingTools = (): JSX.Element => {
    const [validate, setValidate] = useState<number>(0);
    const [scaleWarning, setScaleWarning] = useState<boolean>(true);
    const [drawingWarning, setDrawingWarning] = useState<boolean>(true);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [maxHeight, setMaxHeight] = useState<number>(0);

    const {
        formFeature,
        validateForm,
        onFormUpdated,
        setDrawingInfoVisible,
        drawnFeatures,
        willAttachDrawing,
        setWillAttachDrawing,
        setOverlay,
        setLvmForestryData
    } = useRegistrationContext();
    const { setNewFeature, drawType, setDrawingAllowed, drawingToolsCollapsed } = useDrawingContext();

    const drawTypeRef = useRef<DrawType>(undefined);

    useEffect(() => {
        setTimeout(() => {
            // timeout only when start from this page, because map is not ready yet
            (
                getMap()
                    .getLayers()
                    .getArray()
                    .find((l) => l.get("id") === config.default.drawingLayerId) as VectorLayer<VectorSource>
            )
                .getSource()
                .on("addfeature", (e) => setNewFeature(e.feature));

            getMap().on("moveend", () => setScaleWarning(isDrawingAllowed()));
            setMaxHeight(document.getElementsByClassName("map-container")[0].clientHeight - 100);
        }, 200);
        setDrawingInfoVisible(true);
    }, []);

    useEffect(() => {
        const validateThisForm =
            (validateForm.split("-")[0] as unknown as PageId) === formId ? Number(validateForm.split("-")[1]) : 1;
        if (validateThisForm !== validate) {
            setValidate(validateThisForm);
            !drawingWarning && onFormValid();
        }
    }, [validateForm, validate, drawingWarning]);

    const onFormValid = async () => {
        setOverlay({ isLoading: true });

        let forestryName = "";
        let forestryCode = "";
        let forestryCount = 0;

        const forestry = await getLVMForestry(drawnFeatures);
        if (forestry && forestry.data && forestry.data.LVMFORESTRYNAME && forestry.data.LVMFORESTRYCODE) {
            forestryName = forestry.data.LVMFORESTRYNAME;
            forestryCode = forestry.data.LVMFORESTRYCODE;

            const response = await getIntersectedForestryCount(drawnFeatures);
            response && response.data && response.data.count && (forestryCount = response.data.count);
        } else {
            const forestries = await getIntersectedForestries(drawnFeatures);

            if (forestries && forestries.data && forestries.data.features && forestries.data.features.length) {
                forestryCount = forestries.data.features.length;
                forestryName = forestries.data.features[0].attributes.LVM_FORESTRY_NAME;
                forestryCode = forestries.data.features[0].attributes.LVM_FORESTRY_CODE;
            }
        }
        console.log("forestryName", forestryName, "forestryCode", forestryCode, "forestryCount", forestryCount);
        setLvmForestryData({
            LVMForestryCode: forestryCode,
            LVMForestryCount: forestryCount,
            LVMForestryName: forestryName
        });
        formFeature.set("LVMFORESTRYCODE", forestryCode, true);

        setOverlay(null);
        onFormUpdated();
    };

    useEffect(() => {
        const isRemoteEvent = !!formFeature && formFeature.get("REMOTEEVENT") === 1;
        const hasAnyGeometry = !!drawnFeatures.length;
        const hasGatheringPoint =
            !!drawnFeatures.length && !!drawnFeatures.find((f) => f.get("DRAWTYPE") === DrawType.PointGathering);

        const showDrawingWarning = !(isRemoteEvent ? hasAnyGeometry : hasGatheringPoint);
        setDrawingWarning(showDrawingWarning);
    }, [drawnFeatures]);

    useEffect(() => {
        drawTypeRef.current = drawType;
        setScaleWarning(isDrawingAllowed());
    }, [drawType]);

    useEffect(() => {
        const newWarnings = [];
        scaleWarning &&
            newWarnings.push(
                drawTypeRef.current === DrawType.Polyline || drawTypeRef.current === DrawType.Polygon
                    ? infoStrings.scaleForLineAndAreaWarning
                    : infoStrings.scaleForPointsWarning
            );
        drawingWarning &&
            newWarnings.push(
                !!formFeature && formFeature.get("REMOTEEVENT") === 1
                    ? infoStrings.remoteEventWarning
                    : infoStrings.missingDrawingsWarning
            );
        setWarnings(newWarnings);
    }, [scaleWarning, drawingWarning, drawType]);

    useEffect(() => {
        setDrawingAllowed(!scaleWarning);
    }, [scaleWarning]);

    const isDrawingAllowed = () =>
        !drawTypeRef || drawTypeRef.current === undefined
            ? false
            : drawTypeRef.current === DrawType.Polyline || drawTypeRef.current === DrawType.Polygon
            ? //@ts-ignore
              !(document.mapScale <= 111000) // 2km
            : //@ts-ignore
              !(document.mapScale <= 28000); // 500m

    const onChangeAttachDrawing = (checked: boolean) => {
        setWillAttachDrawing(checked);
    };

    return (
        <>
            <div className={classNames("triangle-top", { "mobile-hidden": drawingToolsCollapsed })}></div>
            <Accordion maxHeight={maxHeight}>
                <div className={classNames("scrollable", { "mobile-collapsed": drawingToolsCollapsed })}>
                    <Search></Search>
                    <DrawPoints></DrawPoints>
                    <DrawPolylines></DrawPolylines>
                    <DrawPolygons></DrawPolygons>
                    <AttachDrawing onChange={onChangeAttachDrawing} defaultChecked={willAttachDrawing}></AttachDrawing>
                </div>
                {warnings.length ? <AccordionInfo warnings={warnings}></AccordionInfo> : null}
            </Accordion>
        </>
    );
};
